import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiService, { Product, PriceHistory, RawPriceData, PriceDemandAnalysis, DebugData, GA4FunnelData, DailyStockData, ProductStockHistory } from '../services/api';
import PriceHistoryTable from '../components/PriceHistoryTable';
import DebugModal from '../components/DebugModal';
import PenaltyV2DebugCard from '../components/PenaltyV2DebugCard';
import ProductRankingTimeline from '../components/ProductRankingTimeline';
import RankingSnapshotsTable from '../components/RankingSnapshotsTable';

const ProductDetailPage: React.FC = () => {
  const { sku } = useParams<{ sku: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [priceHistory, setPriceHistory] = useState<PriceHistory[]>([]);
  const [rawPriceData, setRawPriceData] = useState<RawPriceData[]>([]);
  const [demandAnalysis, setDemandAnalysis] = useState<PriceDemandAnalysis[]>([]);
  const [showRawData, setShowRawData] = useState(false);
  const [showDemandData, setShowDemandData] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugModalOpen, setDebugModalOpen] = useState(false);
  const [debugData, setDebugData] = useState<DebugData | null>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [selectedPrice, setSelectedPrice] = useState<number | null>(null);
  const [ga4Data, setGa4Data] = useState<{[date: string]: GA4FunnelData}>({});
  const [stockData, setStockData] = useState<{[date: string]: DailyStockData}>({});
  const [stockHistory, setStockHistory] = useState<ProductStockHistory[]>([]);
  const [rangeHours] = useState<number>(24);

  useEffect(() => {
    const fetchData = async () => {
      if (!sku) return;

      setLoading(true);
      setError(null);

      try {
        // Fetch product details first (required)
        const productData = await apiService.getProduct(sku);
        setProduct(productData);

        // Fetch other data in parallel, but handle failures gracefully
        const results = await Promise.allSettled([
          apiService.getPriceHistory(sku, { limit: 1000 }),
          apiService.getRawPriceData(sku, { limit: 100 }),
          apiService.getPriceDemandAnalysis(sku),
          apiService.getGA4FunnelData(sku),
          apiService.getDailyStockData(sku),
          apiService.getProductStockHistory(sku, 7)
        ]);

        // Process results individually
        // Price history (index 0)
        if (results[0].status === 'fulfilled') {
          setPriceHistory(results[0].value);
        } else {
          console.warn('Failed to load price history:', results[0].reason);
          setPriceHistory([]);
        }

        // Raw price data (index 1)
        if (results[1].status === 'fulfilled') {
          setRawPriceData(results[1].value);
        } else {
          console.warn('Failed to load raw price data:', results[1].reason);
          setRawPriceData([]);
        }

        // Demand analysis (index 2)
        if (results[2].status === 'fulfilled') {
          setDemandAnalysis(results[2].value);
        } else {
          console.warn('Failed to load demand analysis:', results[2].reason);
          setDemandAnalysis([]);
        }

        // GA4 funnel data (index 3)
        if (results[3].status === 'fulfilled') {
          setGa4Data(results[3].value);
        } else {
          console.warn('Failed to load GA4 funnel data:', results[3].reason);
          setGa4Data({});
        }

        // Daily stock data (index 4)
        if (results[4].status === 'fulfilled') {
          setStockData(results[4].value);
        } else {
          console.warn('Failed to load daily stock data:', results[4].reason);
          setStockData({});
        }

        // Stock history (index 5)
        if (results[5].status === 'fulfilled') {
          setStockHistory(results[5].value);
        } else {
          console.warn('Failed to load stock history:', results[5].reason);
          setStockHistory([]);
        }
      } catch (err) {
        setError('Failed to load product data');
        console.error('Error fetching product data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sku]);

  const formatPrice = (price: number | string | null) => {
    if (price === null || price === undefined) return 'N/A';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return 'N/A';
    return `${numPrice.toFixed(2)}€`;
  };

  const parsePrice = (price: number | string | null): number => {
    if (price === null || price === undefined) return 0;
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return isNaN(numPrice) ? 0 : numPrice;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-PT');
  };

  const getPriceTypeColor = (type: string | null) => {
    switch (type) {
      case 'ZMSR': return 'primary';
      case 'ZPR1': return 'warning';
      case 'ZPM3': return 'success';
      default: return 'secondary';
    }
  };

  // NOTE: This function uses browser's current time (new Date()) to determine if a price row is "active"
  // This is ACCEPTABLE here because:
  // 1. This page shows historical price data, not snapshot-based analysis
  // 2. The "active" flag is just UI visual feedback, doesn't affect calculations
  // 3. We want to show if a price record is currently valid, not at a past snapshot time
  const isActiveRow = (validTo: string | null) => {
    if (!validTo) return true; // Sem data final = ativo
    const today = new Date(); // OK: Used for UI purposes only
    const validToDate = new Date(validTo);
    return validToDate >= today; // Data final superior ou igual a hoje = ativo
  };

  const handlePriceClick = async (price: number) => {
    if (!sku) return;
    
    setSelectedPrice(price);
    setDebugModalOpen(true);
    setDebugLoading(true);
    setDebugData(null);
    
    try {
      const data = await apiService.getDebugDataForPrice(sku, price);
      setDebugData(data);
    } catch (err) {
      console.error('Error fetching debug data:', err);
      setDebugData(null);
    } finally {
      setDebugLoading(false);
    }
  };

  const handleCloseDebugModal = () => {
    setDebugModalOpen(false);
    setDebugData(null);
    setSelectedPrice(null);
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Carregando dados do produto...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="container">
        <div className="alert alert-danger" role="alert">
          {error || 'Produto não encontrado'}
        </div>
        <Link to="/products" className="btn btn-primary">
          Voltar à Lista de Produtos
        </Link>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Back Button */}
      <div className="row">
        <div className="col-12">
          <Link to="/products" className="btn btn-outline-secondary back-button">
            ← Voltar à Lista
          </Link>
        </div>
      </div>

      {/* Product Header */}
      <div className="row">
        <div className="col-12">
          <div className="product-detail-header">
            <div className="row align-items-center">
              <div className="col-md-6">
                <h1 className="mb-2">Histórico de Preços</h1>
                <h2 className="text-primary mb-0">{product.sku}</h2>
              </div>
              <div className="col-md-6 text-md-end">
                <div className="row">
                  <div className="col-6 col-md-3 text-center">
                    <small className="text-muted d-block">Preço Atual</small>
                    <h5 className="text-success mb-0">{formatPrice(product.current_price)}</h5>
                  </div>
                  {priceHistory.length > 0 && (
                    <>
                      <div className="col-6 col-md-3 text-center">
                        <small className="text-muted d-block">Preço Mínimo</small>
                        <h6 className="text-success mb-0">
                          {formatPrice(Math.min(...priceHistory.map(p => parsePrice(p.practiced_price)).filter(p => p > 0)))}
                        </h6>
                      </div>
                      <div className="col-6 col-md-3 text-center">
                        <small className="text-muted d-block">Preço Máximo</small>
                        <h6 className="text-danger mb-0">
                          {formatPrice(Math.max(...priceHistory.map(p => parsePrice(p.practiced_price))))}
                        </h6>
                      </div>
                      <div className="col-6 col-md-3 text-center">
                        <small className="text-muted d-block">Total Registros</small>
                        <h6 className="text-info mb-0">{priceHistory.length}</h6>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Price Legend */}
      <div className="row mb-3">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Legenda dos Tipos de Preço</h5>
              <div className="d-flex flex-wrap gap-3">
                <div className="legend-item">
                  <div className="legend-color" style={{backgroundColor: '#1f77b4'}}></div>
                  ZMSR
                </div>
                <div className="legend-item">
                  <div className="legend-color" style={{backgroundColor: '#ff7f0e'}}></div>
                  ZPR1
                </div>
                <div className="legend-item">
                  <div className="legend-color" style={{backgroundColor: '#2ca02c'}}></div>
                  ZPM3
                </div>
                <div className="legend-item">
                  <div className="legend-color" style={{backgroundColor: '#ffffff', border: '1px solid #dee2e6'}}></div>
                  PRATICADO
                </div>
              </div>
              <small className="text-muted">
                O preço praticado é sempre o menor entre os três tipos de preço disponíveis.
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Price History Table */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Histórico Completo de Preços</h5>
            </div>
            <div className="card-body p-0">
              {priceHistory.length > 0 ? (
                <PriceHistoryTable priceHistory={priceHistory} ga4Data={ga4Data} stockData={stockData} />
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted">Nenhum histórico de preços disponível para este produto.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Ranking Evolution & Debug Section */}
      <div className="row mt-4">
        <div className="col-12">
          <h3 className="mb-3">
            <i className="bi bi-graph-up-arrow me-2"></i>
            Ranking Evolution & Debug
          </h3>
        </div>
      </div>

      {/* Penalty Debug Card & Timeline Chart */}
      <div className="row mt-3">
        <div className="col-12 col-lg-4 mb-3 mb-lg-0">
          <PenaltyV2DebugCard sku={sku || ''} />
        </div>
        <div className="col-12 col-lg-8">
          <ProductRankingTimeline sku={sku || ''} defaultRangeHours={rangeHours} />
        </div>
      </div>

      {/* Ranking Snapshots Table - 📅 Últimos Snapshots de Stock (Tabela RAW) */}
      <div className="row mt-4">
        <div className="col-12">
          <RankingSnapshotsTable sku={sku || ''} rangeHours={rangeHours} />
        </div>
      </div>

      {/* Stock History Section - 📦 Histórico de Stock Raw (Últimos 7 Dias) */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">📦 Histórico de Stock Raw (Últimos 7 Dias, ~3 snapshots/dia)</h5>
            </div>
            <div className="card-body">
              {stockHistory.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm table-hover">
                    <thead>
                      <tr>
                        <th>Data/Hora do Snapshot</th>
                        <th>Planta</th>
                        <th className="text-end">Stock Quantity</th>
                        <th className="text-end">Há</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stockHistory.map((item, index) => {
                        const snapshotDate = new Date(item.snapshot_timestamp);
                        const now = new Date();
                        const diffMs = now.getTime() - snapshotDate.getTime();
                        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                        const diffDays = Math.floor(diffHours / 24);
                        const remainingHours = diffHours % 24;

                        let timeAgo = '';
                        if (diffDays > 0) {
                          timeAgo = `${diffDays}d ${remainingHours}h`;
                        } else {
                          timeAgo = `${diffHours}h`;
                        }

                        return (
                          <tr key={index}>
                            <td>
                              {snapshotDate.toLocaleString('pt-PT', {
                                timeZone: 'Europe/Lisbon',
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </td>
                            <td>
                              <span className="badge bg-secondary">{item.plant}</span>
                            </td>
                            <td className="text-end">
                              <span className={`badge ${item.stock_quantity > 0 ? 'bg-success' : 'bg-danger'}`}>
                                {item.stock_quantity}
                              </span>
                            </td>
                            <td className="text-end text-muted">
                              <small>{timeAgo}</small>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted">Nenhum histórico de stock disponível para este produto.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Performance de Vendas Section */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Performance de Vendas por Preço</h5>
              <button 
                className="btn btn-sm btn-outline-info"
                onClick={() => setShowDemandData(!showDemandData)}
              >
                {showDemandData ? 'Ocultar' : 'Mostrar'} Performance
              </button>
            </div>
            {showDemandData && (
              <div className="card-body">
                {demandAnalysis.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover">
                      <thead>
                        <tr>
                          <th>Preço</th>
                          <th>Qtd. Vendida</th>
                          <th>Receita Total</th>
                          <th>Total Dias</th>
                          <th>Dias com Vendas</th>
                          <th>% Dias c/ Vendas</th>
                          <th>Demanda/Dia</th>
                          <th>Demanda/Dia Vendas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {demandAnalysis.map((item, index) => (
                          <tr key={index}>
                            <td className="fw-bold">
                              <button
                                className="btn btn-link p-0 fw-bold text-decoration-none"
                                style={{ color: 'inherit' }}
                                onClick={() => handlePriceClick(parsePrice(item.practiced_price))}
                                title="Clique para ver dados de debug"
                              >
                                {formatPrice(item.practiced_price)}
                              </button>
                            </td>
                            <td className="text-center">{item.total_quantity_sold.toLocaleString('pt-PT')}</td>
                            <td className="fw-bold text-success">
                              {typeof item.total_revenue === 'number' 
                                ? `${item.total_revenue.toFixed(2)}€`
                                : `${parseFloat(item.total_revenue as string).toFixed(2)}€`
                              }
                            </td>
                            <td className="text-center">{item.total_days_at_price}</td>
                            <td className="text-center">{item.days_with_sales}</td>
                            <td className="text-center">{item.percentage_days_with_sales.toFixed(1)}%</td>
                            <td className="text-center">{item.demand_per_day.toFixed(2)}</td>
                            <td className="text-center">{item.demand_per_sales_day.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <small className="text-muted">
                      Análise de performance de vendas por nível de preço - ordenado por preço (maior para menor)
                    </small>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted">Nenhum dado de performance de vendas disponível para este produto.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Raw Data Section */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Dados Raw do Promoplan</h5>
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={() => setShowRawData(!showRawData)}
              >
                {showRawData ? 'Ocultar' : 'Mostrar'} Dados Raw
              </button>
            </div>
            {showRawData && (
              <div className="card-body">
                {rawPriceData.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover">
                      <thead>
                        <tr>
                          <th>Data</th>
                          <th>Preço Praticado</th>
                          <th>ZMSR</th>
                          <th>ZPR1</th>
                          <th>ZPM3</th>
                          <th>Tipo Ativo</th>
                          <th>Válido De</th>
                          <th>Válido Até</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rawPriceData.map((item, index) => {
                          const isActive = isActiveRow(item.validto);
                          return (
                            <tr 
                              key={index}
                              className={isActive ? 'table-success' : ''}
                              style={isActive ? { backgroundColor: '#d1e7dd' } : {}}
                            >
                              <td>{formatDate(item.date)}</td>
                              <td className="fw-bold">{formatPrice(item.practiced_price)}</td>
                              <td className={item.active_type === 'ZMSR' ? 'fw-bold text-primary' : ''}>
                                {formatPrice(item.zmsr_price)}
                              </td>
                              <td className={item.active_type === 'ZPR1' ? 'fw-bold text-warning' : ''}>
                                {formatPrice(item.zpr1_price)}
                              </td>
                              <td className={item.active_type === 'ZPM3' ? 'fw-bold text-success' : ''}>
                                {formatPrice(item.zpm3_price)}
                              </td>
                              <td>
                                <span className={`badge bg-${getPriceTypeColor(item.active_type)}`}>
                                  {item.active_type}
                                </span>
                              </td>
                              <td className="text-muted small">
                                {item.validfrom ? new Date(item.validfrom).toLocaleString('pt-PT') : 'N/A'}
                              </td>
                              <td className="text-muted small">
                                {item.validto ? new Date(item.validto).toLocaleString('pt-PT') : 'Indefinido'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <small className="text-muted">
                      Mostrando {rawPriceData.length} registros mais recentes dos dados raw do Promoplan
                    </small>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted">Nenhum dado raw disponível para este produto.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Debug Modal */}
      <DebugModal
        isOpen={debugModalOpen}
        onClose={handleCloseDebugModal}
        data={debugData}
        loading={debugLoading}
        price={selectedPrice}
        sku={sku || ''}
      />
    </div>
  );
};

export default ProductDetailPage;